import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Control, Controller, UseFormGetValues } from 'react-hook-form';
import { format } from 'date-fns';
import { useParams } from 'react-router-dom';
import { DATE_FORMAT_EU_DOT, DATE_FORMAT_HOURS_MINUTES, DATE_FORMAT_ISO } from '../../../../../core/constants/date';
import BookingService from '../../../../../core/services/BookingService';
import BookingExternal from '../../../../../core/types/model/BookingExternal';
import { CreateBookingExternalFormData } from '../types';

import Radio from '../../../../../components/form/radio/Radio';
import Table from '../../../../../components/table/Table';
import TR from '../../../../../components/table/TR/TR';
import TH from '../../../../../components/table/TH/TH';
import THead from '../../../../../components/table/THead/THead';
import TBody from '../../../../../components/table/TBody/TBody';
import TD from '../../../../../components/table/TD/TD';
import DeliveryZoneBadge from '../../../../components/delivery-zone-badge/DeliveryZoneBadge';
import NoResultsPage from '../../../../../components/no-results-page/NoResultsPage';
import { formatDate, getTimeDate } from '../../../../../core/utils/date';
import { spawnGlobalNotification } from '../../../../../core/state/global-notifications/asyncActions';
import { GlobalNotificationTypes } from '../../../../../core/enums/GlobalNotificationTypes';
import { Color } from '../../../../../core/enums/Color';
import Icon from '../../../../../components/icon/Icon';
import Button from '../../../../../components/form/button/Button';
import { isMobileScreenSize } from '../../../../../core/utils/isMobileScreenSize';
import { Size } from '../../../../../core/enums/Size';

import './CreateExternalStepTwo.scss';

interface Props {
  control: Control<any>;
  setValue: any;
  getValues: UseFormGetValues<CreateBookingExternalFormData>;
}

const CreateExternalStepTwo: React.FC<Props> = ({ control, setValue, getValues }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { projectId } = useParams<{ projectId: string }>();
  const isMobileScreen = isMobileScreenSize();

  const [freeSlots, setFreeSlots] = useState<BookingExternal[] | null>(null);
  const [showMore, setShowMore] = useState(false);
  const { date: deliveryDate, deliveryDuration, transportEquipmentId, selectedFreeSlot: activeSlot } = getValues();

  const fetchFreeSlots = async () => {
    try {
      const freeSlotsData = await BookingService.getFreeSlotsExternal(
        Number(projectId),
        Number(transportEquipmentId),
        formatDate(new Date(deliveryDate), DATE_FORMAT_ISO),
        Number(deliveryDuration)
      );
      setFreeSlots(freeSlotsData.data.content);
    } catch (e) {
      dispatch(
        spawnGlobalNotification({
          message: String(e),
          type: GlobalNotificationTypes.Error
        })
      );
    }
  };

  useEffect(() => {
    fetchFreeSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterByDeliveryZone = () => {
    return showMore ? freeSlots : freeSlots?.filter((item) => item.date === deliveryDate);
  };

  const generateTableItems = () => {
    return filterByDeliveryZone()?.map(({ date, startTime, endTime, creationIndex, id }) => {
      const selectedFreeSlot = `${id}|${startTime}|${endTime}|${date}`;

      const Slot = () => (
        <Controller
          name="selectedFreeSlot"
          control={control}
          render={({ field: { onChange, name, value } }) => (
            <Radio
              key={id}
              className="pl-1"
              id="selectedFreeSlot"
              name={name}
              options={[{ value: selectedFreeSlot, label: '' }]}
              defaultValue={value}
              onChange={(e) => {
                setValue('startTime', startTime);
                setValue('endTime', endTime);
                setValue('deliveryZoneId', id);
                setValue('activeZone', creationIndex);
                setValue('date', date);
                onChange(e);
              }}
            />
          )}
        />
      );

      return (
        <TR
          className={`create-booking-modal__table-row ${selectedFreeSlot === activeSlot ? 'create-booking-modal__table-row--active' : ''}`}
          key={selectedFreeSlot}
          onClick={() => {
            setValue('startTime', startTime);
            setValue('endTime', endTime);
            setValue('deliveryZoneId', id);
            setValue('activeZone', creationIndex);
            setValue('date', date);
            setValue('selectedFreeSlot', selectedFreeSlot);
          }}
        >
          {!isMobileScreen ? (
            <>
              <TD>
                <span className="create-booking-modal__date pl-2">{format(new Date(date), DATE_FORMAT_EU_DOT)}</span>
              </TD>

              <TD>
                {format(getTimeDate(startTime), DATE_FORMAT_HOURS_MINUTES)} - {format(getTimeDate(endTime), DATE_FORMAT_HOURS_MINUTES)}
              </TD>
              <TD>
                <div className="d-flex justify-content-between">
                  <DeliveryZoneBadge heightAuto labelSize={Size.Large} id={id} />
                  <Slot />
                </div>
              </TD>
            </>
          ) : (
            <>
              <TD>
                <div className="d-flex flex-column pl-3">
                  <span className="create-booking-modal__date">{format(new Date(date), DATE_FORMAT_EU_DOT)}</span>
                  <span>
                    {format(getTimeDate(startTime), DATE_FORMAT_HOURS_MINUTES)} - {format(getTimeDate(endTime), DATE_FORMAT_HOURS_MINUTES)}
                  </span>
                </div>
              </TD>

              <TD>
                <div className="d-flex justify-content-between pr-3">
                  <DeliveryZoneBadge heightAuto size={Size.Medium} id={id} />
                  <Slot />
                </div>
              </TD>
            </>
          )}
        </TR>
      );
    });
  };

  const generateLastRowOfTable = () => {
    return (
      <TR key="table-last-row">
        <TD className="text-center" colSpan={Number(3)}>
          <Button color={Color.Blue} buttonTypes={['transparent']} onClick={() => setShowMore(!showMore)}>
            <span>{!showMore ? t('booking.showMore.modal.label') : t('booking.showLess.modal.label')}</span>
            <Icon
              icon="arrow"
              svg
              size="extra-small"
              className={`create-external-step-two__icon-plus ${showMore ? 'create-external-step-two__icon-plus--desc' : ''}`}
            />
          </Button>
        </TD>
      </TR>
    );
  };

  const generateTableItemsWithShowMoreButton = () => {
    const tableItems = generateTableItems() || [];
    return tableItems.concat(generateLastRowOfTable());
  };

  return (
    <form className={`create-external-step-two create-booking-modal h-100 ${isMobileScreen ? 'create-booking-modal--mobile' : ''}`}>
      <div className="create-booking-modal__content  h-100 d-flex flex-column">
        <p className="input__label">{t('booking.selectAvailableDeliveryTime')}</p>
        <div className="create-booking-modal__table">
          <Table>
            <THead>
              <TR>
                <TH>
                  <span>{!isMobileScreen ? t('common.date') : t('common.dateTime')}</span>
                </TH>
                <TH className={isMobileScreen ? 'd-none' : ''}>
                  <span>{t('common.time')}</span>
                </TH>
                <TH>
                  <span>{t('booking.deliveryZone')}</span>
                </TH>
              </TR>
            </THead>
            <TBody>{freeSlots?.length ? generateTableItemsWithShowMoreButton() : null}</TBody>
          </Table>
        </div>

        {freeSlots?.length === 0 && (
          <NoResultsPage title={t('booking.deliverySlots.noResult')} description={t('booking.deliverySlots.noResult.description')} />
        )}
      </div>
    </form>
  );
};

export default CreateExternalStepTwo;
